import type { ReactNode } from 'react';
import type { PinOffset } from '../../utils/pinCollision';

interface Props {
  /** Where the fan-out put this pin, or undefined when nothing overlapped it. */
  offset: PinOffset | undefined;
  /** Where the real point sits inside the overlay's box, in local pixels. */
  anchor: { x: number; y: number };
  /** Leader line and dot colour — the pin's own, so the pair reads as one thing. */
  color: string;
  children: ReactNode;
}

/**
 * A pin that had to step aside, still tied to the place it belongs to.
 *
 * The overlay stays anchored to the real coordinate and only the pin moves, so
 * the leader line and the dot at the anchor are what the map is actually
 * claiming: *the place is here, the badge is over there because otherwise you
 * would not see it*. A displaced pin without that line would just be wrong.
 */
export default function FannedPin({ offset, anchor, color, children }: Props) {
  if (!offset || (offset.dx === 0 && offset.dy === 0)) return <>{children}</>;

  const length = Math.hypot(offset.dx, offset.dy);
  const angle = Math.atan2(offset.dy, offset.dx);

  return (
    <>
      <span
        aria-hidden
        style={{
          position: 'absolute', left: anchor.x, top: anchor.y,
          width: length, height: 1.5, backgroundColor: color, opacity: 0.55,
          transformOrigin: '0 50%', transform: `rotate(${angle}rad)`,
          pointerEvents: 'none',
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute', left: anchor.x - 2.5, top: anchor.y - 2.5,
          width: 5, height: 5, borderRadius: '50%',
          backgroundColor: color, opacity: 0.8, pointerEvents: 'none',
        }}
      />
      <span style={{ display: 'block', transform: `translate(${offset.dx}px, ${offset.dy}px)` }}>
        {children}
      </span>
    </>
  );
}
